import React, { useEffect, useState } from 'react';
import { ApolloProvider, gql, useMutation, useQuery } from '@apollo/client';
import client from '../lib/apolloClient';
import AppLayout from '../components/Succeedo/Layout/AppLayout';
import UnderstandingPage from '../components/Succeedo/Understanding/UnderstandingPage';
import { UNDERSTANDING_COPY } from '../components/Succeedo/Understanding/copy';

// "What George knows about you" (George P4-2): the current understanding,
// the facts the owner told the coach, and the owner's controls.

export const COACHING_UNDERSTANDING = gql`
  query CoachingUnderstanding {
    coaching_understanding {
      coach_name
      version
      generated_at
      memories
      sharing
      items {
        id
        key
        kind
        kind_label
        statement
        basis
        status
        private
        shareable
        source_labels
      }
    }
  }
`;

export const UNDERSTANDING_FEEDBACK = gql`
  mutation CoachingUnderstandingFeedback($itemKey: String!, $action: String!, $statement: String) {
    coaching_understanding_feedback(item_key: $itemKey, action: $action, statement: $statement) {
      ok
      errors
    }
  }
`;

export const FORGET_MEMORY = gql`
  mutation CoachingForgetMemory($memoryId: ID!) {
    coaching_forget_memory(memory_id: $memoryId) {
      ok
      errors
    }
  }
`;

export const FORGET_EVERYTHING = gql`
  mutation CoachingForgetEverything {
    coaching_forget_everything(confirm: true) {
      ok
      errors
    }
  }
`;

export const UPDATE_SHARING = gql`
  mutation CoachingUpdateSharingConsent($categories: [String!]!, $audience: String) {
    coaching_update_sharing_consent(categories: $categories, audience: $audience) {
      ok
      errors
    }
  }
`;

function track(event) {
  if (typeof window !== 'undefined' && window.ahoy?.track) {
    try { window.ahoy.track(event); } catch (_e) { /* tracking never breaks the page */ }
  }
}

function SucceedoUnderstandingWithData() {
  const { data, loading, error } = useQuery(COACHING_UNDERSTANDING, { fetchPolicy: 'cache-and-network' });
  const refetch = { refetchQueries: [{ query: COACHING_UNDERSTANDING }], awaitRefetchQueries: true };
  const [feedback, { loading: feedbackBusy }] = useMutation(UNDERSTANDING_FEEDBACK, refetch);
  const [forgetMemory, { loading: memoryBusy }] = useMutation(FORGET_MEMORY, refetch);
  const [forgetEverything, { loading: everythingBusy }] = useMutation(FORGET_EVERYTHING, refetch);
  const [updateSharing, { loading: sharingBusy }] = useMutation(UPDATE_SHARING, refetch);
  const [actionError, setActionError] = useState(null);

  useEffect(() => { track('succeedo_george_understanding_viewed'); }, []);

  // Each action resolves true when it worked, so the page can close an editor.
  // With inline, a problem is returned (shown next to the editor) instead of
  // shown at the top of the page.
  const perform = async (mutate, variables, field, { inline = false } = {}) => {
    setActionError(null);
    let problem;
    try {
      const { data: result } = await mutate({ variables });
      const errors = result?.[field]?.errors || [];
      if (errors.length === 0) return true;
      problem = errors.join(' ');
    } catch (_e) {
      problem = UNDERSTANDING_COPY.actionFailed;
    }
    if (inline) return problem;
    setActionError(problem);
    return false;
  };

  const understanding = data?.coaching_understanding || null;

  return (
    <AppLayout breadcrumb={`WHAT ${(understanding?.coach_name || 'George').toUpperCase()} KNOWS`}>
      <UnderstandingPage
        understanding={understanding}
        loading={loading && !understanding}
        error={error}
        busy={feedbackBusy || memoryBusy || everythingBusy || sharingBusy}
        actionError={actionError}
        onConfirm={(itemKey) => perform(feedback, { itemKey, action: 'confirm' }, 'coaching_understanding_feedback')}
        onCorrect={(itemKey, statement) => perform(feedback, { itemKey, action: 'correct', statement }, 'coaching_understanding_feedback', { inline: true })}
        onForget={(itemKey) => perform(feedback, { itemKey, action: 'forget' }, 'coaching_understanding_feedback')}
        onForgetMemory={(memoryId) => perform(forgetMemory, { memoryId }, 'coaching_forget_memory')}
        onForgetEverything={() => perform(forgetEverything, {}, 'coaching_forget_everything')}
        onUpdateSharing={(categories, audience = 'partners') => perform(updateSharing, { categories, audience }, 'coaching_update_sharing_consent')}
      />
    </AppLayout>
  );
}

const SucceedoUnderstandingContainer = (_props, _railsContext) => (
  <ApolloProvider client={client}>
    <SucceedoUnderstandingWithData />
  </ApolloProvider>
);

export default SucceedoUnderstandingContainer;
